/**
 * Configuration: Add variables that should never be suggested
 * Add variable names that should be excluded from suggestions
 */
export const NO_SUGGESTED_VARIABLES = [
  "ENABLE_", // This will match any variable starting with ENABLE_`
  "ATLAS",
  "AWS",
  "DISTRIBUTION_ID",
  "DEBUG_",
  "TEST_",
  // Add more variables or patterns here as needed
];

/**
 * Analyzes a set of variables to identify potential missing variables
 * based on patterns across different prefix groups
 */
export const analyzeMissingVariables = (variables) => {
  // Group variables by their prefixes and core names
  const prefixGroups = new Map(); // Map of prefix -> Set of core names
  const coreToFullMapping = new Map(); // Map of core name -> Set of full variable names
  const variableKeys = new Set(variables.map((v) => v.key));

  // First pass: identify all prefixes and core names
  variables.forEach((v) => {
    const parts = v.key.split("_");
    if (parts.length < 2) return;

    // Try to extract the prefix and core part
    let prefix = "";
    let coreStart = 0;

    // Handle known prefix patterns
    if (parts[0] === "BU" || parts[0] === "GLOBAL") {
      // Handle prefixes like BU_ABROAD, GLOBAL_UAT
      if (parts.length >= 2) {
        prefix = `${parts[0]}_${parts[1]}`;
        coreStart = 2;
      }
    } else {
      // Handle prefixes like COMILLAS_UAT
      if (
        parts.length >= 2 &&
        ["UAT", "PROD", "DEV", "STAGE", "TEST"].includes(parts[1])
      ) {
        prefix = `${parts[0]}_${parts[1]}`;
        coreStart = 2;
      } else {
        prefix = parts[0];
        coreStart = 1;
      }
    }

    if (!prefix || coreStart >= parts.length) return;

    // Extract the core part (everything after the prefix)
    const coreName = parts.slice(coreStart).join("_");
    if (!coreName) return;

    // Add to our maps
    if (!prefixGroups.has(prefix)) {
      prefixGroups.set(prefix, new Set());
    }
    prefixGroups.get(prefix).add(coreName);

    if (!coreToFullMapping.has(coreName)) {
      coreToFullMapping.set(coreName, new Set());
    }
    coreToFullMapping.get(coreName).add(v.key);
  });

  // Find all core names used by at least two different prefixes
  const commonCoreNames = Array.from(coreToFullMapping.entries())
    .filter(([_, fullNames]) => fullNames.size >= 2)
    .map(([coreName]) => coreName);

  // Generate missing variables
  const suggestions = [];
  for (const coreName of commonCoreNames) {
    // For each prefix, check if the core name is used with it
    for (const [prefix] of prefixGroups.entries()) {
      const potentialVar = `${prefix}_${coreName}`;
      if (
        !variableKeys.has(potentialVar) &&
        !suggestions.includes(potentialVar)
      ) {
        // This variable would follow the pattern but doesn't exist
        suggestions.push(potentialVar);
      }
    }
  }

  // Filter out any suggestions that match the NO_SUGGESTED_VARIABLES patterns
  const filteredSuggestions = suggestions.filter((suggestion) => {
    // Check if the suggestion matches any of the patterns in NO_SUGGESTED_VARIABLES
    return !NO_SUGGESTED_VARIABLES.some(
      (pattern) =>
        suggestion.startsWith(pattern) || suggestion.includes(`_${pattern}`)
    );
  });

  // Sort and return suggestions (limited to top 5)
  filteredSuggestions.sort();
  return filteredSuggestions.slice(0, 5);
};
